import { useEffect, useState } from "react";

import moment from "moment";
import { useHistory, useLocation, useParams } from "react-router";

import { toastError } from "@alpacahq/ui/old";

import {
  useGetAsset,
  useGetIsSubscribedToMarketData,
  useGetPositions,
  useGetQuote,
  useMarketDataFeed,
} from "~/v2/api/hooks";
import { FullScreenSpinner } from "~/v2/components/common";
import { OrderTicket } from "~/v2/components/orderTicket";
import { MultiLegOrderTicketConnector } from "~/v2/components/orderTicket/multiLeg";
import { getIsContractSymbol } from "~/v2/components/orderTicket/shared/utils";
import { TVChartContainer } from "~/v2/components/TradingView/TradingViewChartContainer";
import {
  MARKET_DATA_POLLING_INTERVAL,
  POSITIONS_POLLING_INTERVAL,
} from "~/v2/constants";
import { FeatureFlags, useFeatureEnabled } from "~/v2/helpers/featureFlags";
import { isTokenizedEquitySymbol } from "~/v2/helpers/tokenizedEquityTrading";
import { useAccountContext } from "~/v2/providers/AccountProvider";
import type { Asset } from "../../../api/rest";
import type { CryptoAsset } from "../../../api/rest/data";
import { OptionsPromoCard } from "../../../components/marketing/OptionsPromoCard";
import { useIsOptionsOnboardingAvailable } from "../optionsOptIn/util";
import { AssetNotFound } from "./AssetNotFound";
import { amplitudeTradeTracking } from "./amplitudeTradeTracking";
import { ContractDetail } from "./contractDetail/ContractDetail";
import { Hero } from "./Hero";
import { OptionChain } from "./options/OptionChain";
import {
  type OptionContractWithSnapshot,
  useGetOptionsData,
} from "./options/useGetOptionsData";
import { Portfolio } from "./Portfolio";
import { useSyncSearchParams } from "./useSyncSearchParams";
import { ViewControls } from "./ViewControls";

const CRYPTO_EXCHANGE = "ALPACAUS";

const MULTI_LEGS_ENABLED_MINIMUM_OPTIONS_LEVEL = 3;
const MAX_NUM_LEGS = 4;

export type AssetLike = CryptoAsset | Asset;

export type AssetClassTab = "Stocks" | "Options";
export type OptionTypeTab = "Calls" | "Puts" | "Calls & Puts";

export type SelectedContract = {
  contract: OptionContractWithSnapshot;
  price: number | undefined;
  quoteSide?: string;
};

export type ViewState = {
  tab: AssetClassTab;
  optionType: OptionTypeTab;
};

const getInitialTab = (searchAssetClass: string | null): AssetClassTab => {
  switch (searchAssetClass) {
    case "options":
      return "Options";
    default:
      return "Stocks";
  }
};

const getInitialOptionType = (searchType: string | null): OptionTypeTab => {
  switch (searchType) {
    case "calls":
      return "Calls";
    case "puts":
      return "Puts";
    case "callsputs":
      return "Calls & Puts";
    default:
      return "Calls & Puts";
  }
};

export default function TradeV2() {
  const params = useParams<{
    symbol?: string;
    base?: string;
    quote?: string;
  }>();

  const { product, currentAccount } = useAccountContext();

  const isTokenizedEquityTrading = useFeatureEnabled(
    FeatureFlags.TOKENIZED_EQUITY_TRADING,
  );
  const tokenizedEquityDataApiUrl = window.env.TOKENIZED_EQUITY_DATA_API_URL;

  const { isSubscribed: isSubscribedToMarketData } =
    useGetIsSubscribedToMarketData();

  const history = useHistory();
  const location = useLocation();
  const search = new URLSearchParams(location.search);

  const [selectedContracts, setSelectedContracts] = useState<
    SelectedContract[]
  >([]);

  const isCrypto = !!params.base && !!params.quote;

  const contractSymbol = search.get("contract");

  const symbol =
    (isCrypto ? `${params.base}/${params.quote}` : params.symbol) || "";

  const [viewState, setViewState] = useState<ViewState>({
    tab: getInitialTab(search.get("asset_class")),
    optionType: getInitialOptionType(search.get("type")),
  });

  const [selectedExpiration, setSelectedExpiration] = useState(
    search.get("expiration") || "",
  );

  const { asset, isFetched: isAssetFetched } = useGetAsset({
    symbol,
    options: { enabled: !!symbol },
  });

  const { positions, refetch: refetchPositions } = useGetPositions({
    accountID: currentAccount?.id,
    product,
    options: {
      enabled: !!currentAccount?.id,
      refetchInterval: POSITIONS_POLLING_INTERVAL,
    },
  });

  const { latestFeed } = useMarketDataFeed(
    asset?.attributes?.includes("overnight_tradable"),
  );

  const { quote } = useGetQuote({
    symbol: asset?.symbol,
    feed: latestFeed,
    options: {
      enabled: !!asset?.symbol,
      refetchInterval: MARKET_DATA_POLLING_INTERVAL,
      staleTime: MARKET_DATA_POLLING_INTERVAL * 3,
    },
  });

  const {
    expirations,
    optionContracts,
    isContractsFetchedAfterMount,
    isContractsRefetching,
  } = useGetOptionsData({
    asset,
    expirationDate: selectedExpiration,
    isCrypto,
  });

  const { isOptionsApproved, optionsApprovedLevel } =
    useIsOptionsOnboardingAvailable();

  const maxSelectableContracts =
    // paper always gets max
    product === "paper" ||
    // live gets max if options approved level meets requirement
    (isOptionsApproved &&
      optionsApprovedLevel >= MULTI_LEGS_ENABLED_MINIMUM_OPTIONS_LEVEL)
      ? MAX_NUM_LEGS
      : 1;

  const onViewChange = (view: Partial<ViewState>) => {
    setViewState((prev) => ({
      ...prev,
      ...view,
    }));

    if (view?.tab === "Options") {
      amplitudeTradeTracking.optionsTabClicked();
    }
    if (view?.optionType) {
      amplitudeTradeTracking.optionsCallsPutsClicked(
        view.optionType.toLowerCase() as "call" | "put" | "calls & puts",
      );
    }
  };

  const onOptionContractClicked = async (
    contract: OptionContractWithSnapshot,
    price?: number,
    quoteSide?: string,
  ) => {
    setSelectedContracts((prevContracts) => {
      // Toggle selection
      if (
        prevContracts.some(
          (prevContract) =>
            prevContract.contract.symbol === contract.symbol &&
            prevContract.quoteSide === quoteSide,
        )
      ) {
        return prevContracts.filter(
          (prevContract) =>
            !(
              prevContract.contract.symbol === contract.symbol &&
              prevContract.quoteSide === quoteSide
            ),
        );
      }

      // switch to new selected contract if max is 1
      if (maxSelectableContracts === 1) {
        return [{ contract, price, quoteSide }];
      }

      // check max selectable
      if (
        prevContracts.length + 1 > maxSelectableContracts &&
        // but allow changing quoteSide of same symbol
        !prevContracts.some(
          ({ contract: { symbol } }) => symbol === contract.symbol,
        )
      ) {
        toastError(
          `Cannot add additional leg to order. Maximum number of legs: ${maxSelectableContracts}`,
          {},
        );
        return prevContracts;
      }

      return [
        // filter duplicate symbols - user could be changing quoteSide
        ...prevContracts.filter(
          (prevContract) => prevContract.contract.symbol !== contract.symbol,
        ),
        { contract, price, quoteSide },
      ];
    });

    amplitudeTradeTracking.optionsContractClicked(contract.symbol, quoteSide);
  };

  const handleRemoveContract = (symbol: string) =>
    setSelectedContracts((contracts) =>
      contracts.filter(({ contract }) => symbol !== contract.symbol),
    );

  const handleUpdateLegQuoteSide = (symbol: string, quoteSide: string) =>
    setSelectedContracts((prevState) =>
      prevState.map((c) =>
        c.contract.symbol === symbol ? { ...c, quoteSide } : c,
      ),
    );

  const onExpirationSelected = (expiration: string) => {
    setSelectedExpiration(expiration);
    amplitudeTradeTracking.optionsExpirationClicked(expiration);
  };

  useEffect(() => {
    if (expirations.length && !selectedExpiration) {
      // we want the closest expiration selected by default
      setSelectedExpiration(expirations[0]);
    }
  }, [expirations, selectedExpiration]);

  useSyncSearchParams({
    symbol,
    isCrypto,
    view: viewState,
    expiration: selectedExpiration,
  });

  // If the symbol is actually a Contract symbol get the underlying symbol
  // and push to that Trade page
  useEffect(() => {
    // Symbol must be same as asset symbol for OrderTicket, ie BTCUSD -> BTC/USD
    if (asset && asset.symbol !== symbol) {
      history.replace(`/trade/${asset.symbol}`);
      return;
    }

    if (getIsContractSymbol(symbol)) {
      const underlyingSymbol = symbol.substring(0, symbol.search(/\d/));

      if (underlyingSymbol) {
        history.replace(`/trade/${underlyingSymbol}?contract=${symbol}`);
      }
    }
  }, [symbol, asset, history.replace]);

  useEffect(() => {
    if (!selectedContracts.length) {
      return;
    }

    setSelectedContracts((prevContracts) =>
      prevContracts.map((prevContract) => ({
        ...prevContract,
        contract:
          optionContracts.find(
            ({ symbol }) => symbol === prevContract.contract.symbol,
          ) ?? prevContract.contract,
      })),
    );
  }, [optionContracts, selectedContracts.length]);

  if (!product || !currentAccount || !isAssetFetched) {
    return <FullScreenSpinner />;
  }

  if (!asset) {
    return <AssetNotFound />;
  }

  const usesTokenizedEquityMarketData =
    isTokenizedEquityTrading &&
    isTokenizedEquitySymbol(asset.symbol) &&
    !!tokenizedEquityDataApiUrl;

  return (
    <div className="pr-6 pb-20 pl-6 xs:pl-0">
      <Hero asset={asset} quote={quote} />
      {contractSymbol ? (
        <div>
          <ContractDetail
            contractSymbol={contractSymbol}
            quote={quote}
            positions={positions}
            product={product}
            asset={asset}
            account={currentAccount}
          />
        </div>
      ) : (
        <div>
          <div className="flex flex-col gap-4 md:flex-row">
            <div className="flex-1 space-y-4 overflow-x-auto">
              <ViewControls
                isCrypto={
                  asset.class === "crypto" || usesTokenizedEquityMarketData
                }
                viewState={viewState}
                onViewChange={onViewChange}
                selectedExpiration={selectedExpiration}
                onExpirationSelected={onExpirationSelected}
                expirations={expirations}
              />
              {viewState.tab === "Options" ? (
                /** OptionsChain for Options */
                <OptionChain
                  isLoading={
                    isContractsFetchedAfterMount && !isContractsRefetching
                  }
                  quote={quote}
                  optionContracts={optionContracts}
                  expiration={selectedExpiration}
                  optionsType={viewState.optionType}
                  onClick={onOptionContractClicked}
                  selectedContracts={selectedContracts.map((s) => ({
                    symbol: s.contract.symbol,
                    type: s.quoteSide ?? "",
                  }))}
                />
              ) : (
                /** TradingView Chart for Stocks and Crypto */
                <TVChartContainer
                  seriesStyle={1}
                  key={asset?.symbol}
                  symbol={asset?.symbol}
                  crypto={
                    asset.class === "crypto" || usesTokenizedEquityMarketData
                  }
                  utcOffset={moment().format("Z")}
                  interval="1"
                  enabled_features={[
                    "use_localstorage_for_settings",
                    "save_chart_properties_to_local_storage",
                    "remove_library_container_border",
                    "pre_post_market_sessions",
                    "disable_legend_inplace_symbol_change",
                  ]}
                  disabled_features={[
                    "main_series_scale_menu",
                    "header_saveload",
                    "header_compare",
                    "header_undo_redo",
                    "display_market_status",
                    "left_toolbar",
                    "header_screenshot",
                    "header_settings",
                    "symbol_search_hot_key",
                    "header_quick_search",
                    "header_symbol_search",
                    "allow_arbitrary_symbol_search_input",
                  ]}
                  exchange={
                    asset.class === "crypto" || usesTokenizedEquityMarketData
                      ? CRYPTO_EXCHANGE
                      : asset?.exchange
                  }
                  baseUrl={
                    usesTokenizedEquityMarketData
                      ? tokenizedEquityDataApiUrl
                      : asset.class === "crypto"
                        ? window.env.CRYPTO_DATA_API_URL
                        : window.env.DATA_API_URL
                  }
                  cryptoDataApiUrl={
                    usesTokenizedEquityMarketData
                      ? tokenizedEquityDataApiUrl
                      : undefined
                  }
                  height={450}
                  isUnlimited={isSubscribedToMarketData}
                  isOvernightTradable={
                    !!asset?.attributes?.includes("overnight_tradable")
                  }
                />
              )}
              <Portfolio
                product={product}
                account={currentAccount}
                asset={asset}
                positions={{
                  list: positions,
                  refetch: refetchPositions,
                }}
              />
            </div>
            <div className="w-full md:w-[368px]">
              {viewState.tab === "Options" ? (
                !isOptionsApproved ? (
                  <OptionsPromoCard />
                ) : selectedContracts.length > 1 ? (
                  <MultiLegOrderTicketConnector
                    underlyingSymbol={symbol}
                    legs={selectedContracts}
                    onRemoveLeg={handleRemoveContract}
                    onUpdateLegType={handleUpdateLegQuoteSide}
                  />
                ) : (
                  <OrderTicket
                    symbol={selectedContracts.at(0)?.contract?.symbol ?? symbol}
                    price={selectedContracts.at(0)?.price}
                    quoteSide={selectedContracts.at(0)?.quoteSide}
                    onCancel={
                      selectedContracts.length > 0
                        ? () => setSelectedContracts([])
                        : undefined
                    }
                  />
                )
              ) : (
                <OrderTicket symbol={symbol} />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
